(() => {
  'use strict';

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[character]);

  const formatMoney = (cents) => new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR'
  }).format(cents / 100);

  document.querySelectorAll('[data-hc-wholesale][data-approved="true"]').forEach((root) => {
    const minimum = Number(root.dataset.minimumItems || 10);
    const discountOneThreshold = Number(root.dataset.discountOneThreshold || 100000);
    const discountOnePercent = Number(root.dataset.discountOnePercent || 5);
    const discountTwoThreshold = Number(root.dataset.discountTwoThreshold || 300000);
    const discountTwoPercent = Number(root.dataset.discountTwoPercent || 10);
    const draftOrderEndpoint = root.dataset.draftOrderEndpoint || '/apps/hc-wholesale-order';
    const customerEmail = root.dataset.customerEmail || '';
    const inputs = [...root.querySelectorAll('.hc-size-input')];
    const orderDialog = root.querySelector('[data-order-dialog]');
    const detailsDialog = root.querySelector('[data-details-dialog]');
    const detailsForm = root.querySelector('[data-details-form]');
    const linesEl = root.querySelector('[data-order-lines]');
    const confirmButton = root.querySelector('[data-order-confirm]');
    const orderStatus = root.querySelector('[data-order-status]');
    const minimumMessage = root.querySelector('[data-minimum-message]');
    const detailsError = root.querySelector('[data-details-error]');
    const sendButton = root.querySelector('[data-send-order]');
    const storageKey = `hc-wholesale-1b:${window.location.pathname}`;

    const discountPercent = (subtotalCents) => {
      if (subtotalCents >= discountTwoThreshold) return discountTwoPercent;
      if (subtotalCents >= discountOneThreshold) return discountOnePercent;
      return 0;
    };

    const state = () => {
      const lines = inputs
        .map((input) => ({
          input,
          variantId: Number(input.dataset.variantId),
          article: input.dataset.article || input.dataset.sku || '',
          product: input.dataset.product || '',
          colour: input.dataset.colour || '',
          size: input.dataset.size || '',
          sku: input.dataset.sku || '',
          priceCents: Number(input.dataset.priceCents || 0),
          quantity: Math.max(0, Math.min(500, Number.parseInt(input.value || '0', 10) || 0))
        }))
        .filter((line) => line.quantity > 0 && line.article);
      const units = lines.reduce((sum, line) => sum + line.quantity, 0);
      const subtotalCents = lines.reduce((sum, line) => sum + (line.priceCents * line.quantity), 0);
      const percent = discountPercent(subtotalCents);
      const discountCents = Math.round(subtotalCents * percent / 100);
      return { lines, units, subtotalCents, percent, discountCents, totalCents: subtotalCents - discountCents };
    };

    const setText = (selector, value) => {
      root.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
    };

    const saveOrder = () => {
      const quantities = {};
      inputs.forEach((input) => {
        const quantity = Math.max(0, Math.min(500, Number.parseInt(input.value || '0', 10) || 0));
        if (quantity) quantities[String(input.dataset.variantId)] = quantity;
      });
      try { window.sessionStorage.setItem(storageKey, JSON.stringify(quantities)); } catch (error) { /* Ordering still works. */ }
    };

    const loadOrder = () => {
      try {
        const quantities = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
        inputs.forEach((input) => {
          const quantity = Number.parseInt(quantities[String(input.dataset.variantId)] || '0', 10) || 0;
          input.value = String(Math.max(0, Math.min(500, quantity)));
        });
      } catch (error) { /* Start with an empty order. */ }
    };

    const render = () => {
      const order = state();
      setText('[data-order-items], [data-dialog-items]', String(order.units));
      setText('[data-order-subtotal], [data-dialog-subtotal]', formatMoney(order.subtotalCents));
      setText('[data-order-discount], [data-dialog-discount]', order.percent ? `${order.percent}% · −${formatMoney(order.discountCents)}` : '0%');
      setText('[data-order-total], [data-dialog-total]', formatMoney(order.totalCents));

      const missing = Math.max(0, minimum - order.units);
      minimumMessage.textContent = order.units >= minimum
        ? 'Minimum reached — your preorder is ready to confirm.'
        : `Add ${missing} more item${missing === 1 ? '' : 's'} to reach the minimum order.`;
      minimumMessage.classList.toggle('is-ready', order.units >= minimum);
      confirmButton.disabled = order.units < minimum;

      if (!order.lines.length) {
        linesEl.innerHTML = '<p>NO PRODUCTS SELECTED YET.</p>';
        return;
      }

      linesEl.innerHTML = order.lines.map((line) => `
        <div class="hc-order-line">
          <strong>${escapeHtml(line.product)}</strong>
          <span data-line-colour>${escapeHtml(line.colour)}</span>
          <span data-line-sku>${escapeHtml(line.sku || line.article)}</span>
          <span>${escapeHtml(line.size)}</span>
          <span>${line.quantity}</span>
          <strong>${formatMoney(line.priceCents * line.quantity)}</strong>
          <button type="button" data-remove-variant="${line.variantId}" aria-label="Remove ${escapeHtml(line.product)} size ${escapeHtml(line.size)}">×</button>
        </div>
      `).join('');
    };

    const openDialog = (dialog) => {
      if (typeof dialog?.showModal === 'function') dialog.showModal();
      else dialog?.setAttribute('open', '');
    };

    const closeDialog = (dialog) => {
      if (typeof dialog?.close === 'function' && dialog.open) dialog.close();
      else dialog?.removeAttribute('open');
    };

    const getSubmissionId = () => {
      const key = `${storageKey}:submission`;
      let id = '';
      try { id = window.sessionStorage.getItem(key) || ''; } catch (error) { /* Create a fresh ID. */ }
      if (/^[a-zA-Z0-9-]{16,64}$/.test(id)) return id;
      id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      try { window.sessionStorage.setItem(key, id); } catch (error) { /* Submission still works. */ }
      return id;
    };

    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        input.value = String(Math.max(0, Math.min(500, Number.parseInt(input.value || '0', 10) || 0)));
        saveOrder();
        render();
      });
    });

    root.querySelectorAll('[data-gallery]').forEach((gallery) => {
      const track = gallery.querySelector('[data-gallery-track]');
      const move = (direction) => track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => move(-1));
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => move(1));
    });

    root.querySelectorAll('[data-order-open]').forEach((button) => {
      button.addEventListener('click', () => {
        render();
        openDialog(orderDialog);
      });
    });

    linesEl?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-remove-variant]');
      if (!button) return;
      inputs
        .filter((input) => input.dataset.variantId === button.dataset.removeVariant)
        .forEach((input) => { input.value = '0'; });
      saveOrder();
      render();
    });

    root.querySelector('[data-order-clear]')?.addEventListener('click', () => {
      inputs.forEach((input) => { input.value = '0'; });
      orderStatus.textContent = '';
      saveOrder();
      render();
    });

    confirmButton?.addEventListener('click', () => {
      if (state().units < minimum) return;
      closeDialog(orderDialog);
      if (detailsError) detailsError.hidden = true;
      openDialog(detailsDialog);
    });

    root.querySelectorAll('[data-details-close]').forEach((button) => {
      button.addEventListener('click', () => closeDialog(detailsDialog));
    });

    detailsForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const order = state();
      if (order.units < minimum || !detailsForm.reportValidity()) return;

      const formData = new FormData(detailsForm);
      const vatNumber = String(formData.get('vatNumber') || '').trim().toUpperCase();
      detailsForm.elements.vatNumber.value = vatNumber;
      detailsError.hidden = true;
      sendButton.disabled = true;
      sendButton.textContent = 'SENDING ORDER…';

      try {
        const response = await fetch(draftOrderEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            submissionId: getSubmissionId(),
            customerEmail,
            vatNumber,
            shippingAddress: {
              firstName: String(formData.get('firstName') || '').trim(),
              lastName: String(formData.get('lastName') || '').trim(),
              company: String(formData.get('company') || '').trim(),
              address1: String(formData.get('address1') || '').trim(),
              address2: String(formData.get('address2') || '').trim(),
              zip: String(formData.get('zip') || '').trim(),
              city: String(formData.get('city') || '').trim(),
              countryCode: String(formData.get('countryCode') || '').trim().toUpperCase(),
              phone: String(formData.get('phone') || '').trim()
            },
            lines: order.lines.map((line) => ({
              article: line.article,
              size: line.size,
              quantity: line.quantity
            }))
          })
        });
        const contentType = response.headers.get('content-type') || '';
        const result = contentType.includes('application/json') ? await response.json() : null;
        if (!response.ok || !result?.ok) {
          throw new Error(result?.error || 'The Shopify Draft Order connection is not active yet.');
        }

        inputs.forEach((input) => { input.value = '0'; });
        try {
          window.sessionStorage.removeItem(storageKey);
          window.sessionStorage.removeItem(`${storageKey}:submission`);
        } catch (error) { /* Order was already submitted. */ }
        closeDialog(detailsDialog);
        orderStatus.textContent = `ORDER RECEIVED${result.draftOrder?.name ? ` — ${result.draftOrder.name}` : ''}. We will contact you with confirmation.`;
        render();
        openDialog(orderDialog);
      } catch (error) {
        detailsError.textContent = error?.message || 'The order could not be sent. Please try again.';
        detailsError.hidden = false;
      } finally {
        sendButton.disabled = false;
        sendButton.textContent = 'CONFIRM ORDER';
      }
    });

    loadOrder();
    render();
  });
})();
